import { Injectable, signal, computed } from '@angular/core';

/**
 * FilingModeService
 *
 * Centralized service to manage filing form mode state across all role components.
 *
 * Purpose:
 * - Holds the current conversionId from route parameters
 * - Provides computed isEditedMode signal based on conversionId existence
 * - Single source of truth for edit mode state
 *
 * Mode Logic:
 * - First Time Mode: conversionId is null/empty → hide asterisks (all fields mandatory anyway)
 * - Edited Mode: conversionId exists → show asterisks ("Optional: Suggestions to refine data")
 *
 * Usage:
 * 1. filing-form-container calls setConversionId(id) when route params change
 * 2. All role components inject this service and read isEditedMode()
 * 3. Templates conditionally show asterisks: *ngIf="isEditedMode() && isFieldRequired(control)"
 */
@Injectable({
  providedIn: 'root'
})
export class FilingModeService {
  /**
   * Private signal holding the current conversionId
   * Updated by filing-form-container when route parameters change
   */
  private conversionIdSignal = signal<string | null>(null);

  /**
   * Public computed signal - determines if user is in "edited mode"
   *
   * Returns true when:
   * - conversionId exists (not null)
   * - conversionId is not empty string
   *
   * Returns false when:
   * - conversionId is null (new filing)
   * - conversionId is empty string (edge case)
   */
  isEditedMode = computed(() => {
    const id = this.conversionIdSignal();
    return !!id && id.length > 0;
  });

  /**
   * Update the conversionId
   * Called by filing-form-container when route parameters change
   *
   * @param id - The conversionId from route parameters (null for new filings)
   */
  setConversionId(id: string | null): void {
    this.conversionIdSignal.set(id);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('[FilingModeService] conversionId updated:', id);
    // console.log('[FilingModeService] isEditedMode:', this.isEditedMode());
  }

  /**
   * Get the current conversionId value
   *
   * @returns Current conversionId or null
   */
  getConversionId(): string | null {
    return this.conversionIdSignal();
  }

  /**
   * Check if currently in edited mode (without using computed signal)
   * Useful for imperative checks in TypeScript code
   *
   * @returns true if in edited mode, false otherwise
   */
  checkIsEditedMode(): boolean {
    const id = this.conversionIdSignal();
    return !!id && id.length > 0;
  }
}
